(() => {
  const DESKTOP_QUERY = '(min-width: 990px)';

  class GardenDiscovery {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector('[data-garden-track]');
      this.slides = Array.from(section.querySelectorAll('[data-garden-slide]'));
      this.indexLinks = Array.from(section.querySelectorAll('[data-garden-index-link]'));
      this.previousButton = section.querySelector('[data-garden-previous]');
      this.nextButton = section.querySelector('[data-garden-next]');
      this.position = section.querySelector('[data-garden-position]');
      this.desktopMedia = window.matchMedia(DESKTOP_QUERY);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.activeIndex = 0;
      this.scrollFrame = null;

      if (!this.track || this.slides.length === 0 || this.indexLinks.length === 0) return;

      this.bindEvents();
      this.setActive(0, { scroll: false });
      section.dataset.gardenDiscoveryReady = 'true';
    }

    bindEvents() {
      this.indexLinks.forEach((link) => {
        const index = Number(link.dataset.index);
        link.addEventListener('pointerenter', () => {
          if (this.desktopMedia.matches) this.setActive(index, { scroll: false });
        });
        link.addEventListener('focus', () => {
          if (this.desktopMedia.matches) this.setActive(index, { scroll: false });
        });
      });

      this.previousButton?.addEventListener('click', () => this.step(-1));
      this.nextButton?.addEventListener('click', () => this.step(1));

      this.track.addEventListener(
        'scroll',
        () => {
          if (this.desktopMedia.matches || this.scrollFrame) return;
          this.scrollFrame = window.requestAnimationFrame(() => {
            this.scrollFrame = null;
            this.syncFromScroll();
          });
        },
        { passive: true }
      );

      this.desktopMedia.addEventListener('change', () => {
        this.setActive(this.activeIndex, { scroll: !this.desktopMedia.matches, immediate: true });
      });
    }

    step(direction) {
      const nextIndex = Math.min(Math.max(this.activeIndex + direction, 0), this.slides.length - 1);
      if (nextIndex === this.activeIndex) return;
      this.setActive(nextIndex, { scroll: !this.desktopMedia.matches });
    }

    setActive(index, options = {}) {
      if (!Number.isInteger(index) || index < 0 || index >= this.slides.length) return;

      this.activeIndex = index;
      const isDesktop = this.desktopMedia.matches;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        slide.classList.toggle('is-active', isActive);
        slide.tabIndex = isDesktop && !isActive ? -1 : 0;
        slide.setAttribute('aria-hidden', isDesktop && !isActive ? 'true' : 'false');
      });

      this.indexLinks.forEach((link, linkIndex) => {
        link.classList.toggle('is-active', linkIndex === index);
      });

      if (this.position) {
        this.position.textContent = `${String(index + 1).padStart(2, '0')} / ${String(this.slides.length).padStart(2, '0')}`;
      }

      if (this.previousButton) this.previousButton.disabled = index === 0;
      if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;

      if (options.scroll && !isDesktop) this.scrollToSlide(index, options.immediate);
    }

    scrollToSlide(index, immediate = false) {
      const slide = this.slides[index];
      const trackRect = this.track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const left = this.track.scrollLeft + slideRect.left - trackRect.left;
      this.track.scrollTo({
        left,
        behavior: immediate || this.reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    syncFromScroll() {
      const trackRect = this.track.getBoundingClientRect();
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - trackRect.left);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== this.activeIndex) this.setActive(closestIndex, { scroll: false });
    }
  }

  const initializeGardenDiscovery = (root = document) => {
    root.querySelectorAll('[data-garden-discovery]:not([data-garden-discovery-ready])').forEach((section) => {
      new GardenDiscovery(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeGardenDiscovery());
  } else {
    initializeGardenDiscovery();
  }

  document.addEventListener('shopify:section:load', (event) => initializeGardenDiscovery(event.target));
})();
